import React, { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { useReactTable, getCoreRowModel, createColumnHelper } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import StatCard from "./ReportsCards";
import { Supplier } from "types";

import { getAuthCookie } from "../utils/authCookies";

type CardsSummary = {
  total_orders: number;
  total_revenue: number;
  amount_paid: number;
  outstanding_balance: number;
};

interface Row {
  order_id: string;
  date: string;
  property_address?: string;
  status: number | string;
  supplier: string;
  amount: number;
  paid: number;
  balance: number;
  over_paid: boolean;
  payment_method: string;
}

type SelectOption = { label: string; value: string };
type StatusOption = { label: string; value: number };

const DATE_OPTIONS: SelectOption[] = [
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 15 Days", value: "last_15_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom", value: "custom" },
];

const PAYMENT_STATUS_OPTIONS: SelectOption[] = [
  { label: "All", value: "all" },
  { label: "Paid", value: "Paid" },
  { label: "Partial Payment", value: "Partial Payment" },
  { label: "Pending", value: "Pending" },
  { label: "Overdue", value: "Overdue" },
];

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
  { label: "All", value: "all" },
  { label: "Card", value: "Card" },
  { label: "Net 30", value: "Net 30" },
  { label: "Bank Transfer", value: "Bank Transfer" },
];

/** Full status list (multi-select). Default: [] → do NOT send any statuses */
const STATUS_OPTIONS: StatusOption[] = [
  { label: "Draft",        value: 100 }, // RemovalOrderDraft
  { label: "Finalized",    value: 200 }, // RemovalOrderFinalized
  { label: "Scheduled",    value: 300 }, // RemovalOrderScheduled
  { label: "In Progress",  value: 400 }, // RemovalOrderInProgress
  { label: "Completed",    value: 500 }, // RemovalOrderCompleted
  { label: "Closed",       value: 600 }, // RemovalOrderClosed
  { label: "Canceled",     value: 700 }, // RemovalOrderCanceled
  { label: "Void",         value: 800 }, // RemovalOrderVoid
];

const STATUS_LABEL = (code?: number | string) => {
  const n = typeof code === "string" ? Number(code) : code;
  return STATUS_OPTIONS.find(s => s.value === n)?.label ?? String(code ?? "--");
};

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
  multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
  menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};

export default function AgentRemovalSummaryReport() {
  const token = getAuthCookie("sessionToken");
  const loginId = getAuthCookie("userId");

  const fmtMoney = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(n || 0));

  // data + table
  const [data, setData] = useState<Row[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // filters
  const [datePreset, setDatePreset] = useState<SelectOption>(DATE_OPTIONS[2]);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>([]); // ⬅️ default empty (send nothing)
  const [paymentStatus, setPaymentStatus] = useState<SelectOption>(PAYMENT_STATUS_OPTIONS[0]);
  const [paymentMethod, setPaymentMethod] = useState<SelectOption>(PAYMENT_METHOD_OPTIONS[0]);

  // suppliers
  const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([{ label: "All", value: "" }]);
  const [selectedSupplier, setSelectedSupplier] = useState<SelectOption>({ label: "All", value: "" });

  // cards
  const [cards, setCards] = useState<CardsSummary>({
    total_orders: 0,
    total_revenue: 0,
    amount_paid: 0,
    outstanding_balance: 0,
  });

  // UI state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const topRef = useRef<HTMLDivElement>(null);
  
  // Calculate date range limits (last 6 months from today)
  const dateRangeLimits = useMemo(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    
    // Format as YYYY-MM-DD for date input
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      minDate: formatDate(sixMonthsAgo),
      maxDate: formatDate(today)
    };
  }, []);

  // Validate and reset dates when custom is selected or dates are out of range
  useEffect(() => {
    if (datePreset.value === "custom") {
      const minDate = dateRangeLimits.minDate;
      const maxDate = dateRangeLimits.maxDate;
      
      // Reset From date if it's outside the valid range
      if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
        setCustomFrom("");
      }
      
      // Reset To date if it's outside the valid range
      if (customTo && (customTo < minDate || customTo > maxDate)) {
        setCustomTo("");
      }
    }
  }, [datePreset.value, dateRangeLimits, customFrom, customTo]);

  // actions
  const [generateClicked, setGenerateClicked] = useState(false);
  function handleGenerate() {
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setGenerateClicked((v) => !v);
  }
  function clearFilters() {
    setDatePreset(DATE_OPTIONS[2]);
    setCustomFrom(""); setCustomTo("");
    setSelectedStatuses([]); // ⬅️ empty
    setPaymentStatus(PAYMENT_STATUS_OPTIONS[0]);
    setPaymentMethod(PAYMENT_METHOD_OPTIONS[0]);
    setSelectedSupplier({ label: "All", value: "" });
    setSearchInput("");
    setGlobalFilter("");
    setPagination({ pageIndex: 0, pageSize: 10 });
    setGenerateClicked((v) => !v);
  }

  // suppliers for this agent
  useEffect(() => {
    const run = async () => {
      if (!loginId) return;
      try {
        const r = await fetch(`${process.env.REACT_APP_API_URL}/agent/suppliers/${loginId}`, {
          headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
        });
        if (!r.ok) throw new Error();
        const list: Supplier[] = await r.json();
        const opts = [{ label: "All", value: "" }, ...list.map((s: any) => ({ label: s.name ?? s.title, value: s.supplier_id ?? s.id }))];
        setSupplierOptions(opts);
        setSelectedSupplier(opts[0]);
      } catch {
        setSupplierOptions([{ label: "All", value: "" }]);
        setSelectedSupplier({ label: "All", value: "" });
      }
    };
    run();
  }, [loginId, token]);

  // fetch report
  async function fetchData(signal?: AbortSignal) {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/reports/agent/removal_orders_summary`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
          signal,
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            // ⬇️ send selected codes only; default [] (nothing)
            status: selectedStatuses.map(s => s.value),
            // payload expects array for payment_status (empty = no filter)
            payment_status: paymentStatus.value === "all" ? [] : [paymentStatus.value],
            payment_method: paymentMethod.value === "all" ? "" : paymentMethod.value,
            supplier_id: selectedSupplier.value || "",
            search: globalFilter,
            date_range: {
              type: datePreset.value,
              from: datePreset.value === "custom" ? customFrom : "",
              to: datePreset.value === "custom" ? customTo : "",
            },
          }),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        toast.error(result?.error || "Failed to fetch agent removal summary.");
        setData([]); setRowCount(0);
        setCards({ total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 });
        return;
      }

      const rows: Row[] = Array.isArray(result?.data) ? result.data : [];
      setData(rows);
      setRowCount(Number(result?.total || rows.length || 0));

      const totals = rows.reduce(
        (acc, r) => {
          acc.total_orders += 1;
          acc.total_revenue += Number(r.amount || 0);
          acc.amount_paid += Number(r.paid || 0);
          acc.outstanding_balance += Number(r.balance || 0);
          return acc;
        },
        { total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 }
      );
      setCards(totals);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      toast.error("Network error while fetching agent removal summary.");
      setData([]); setRowCount(0);
      setCards({ total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 });
    }
  }

  useEffect(() => {
    const ac = new AbortController();
    fetchData(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line
  }, [generateClicked, pagination.pageIndex, pagination.pageSize, globalFilter]);

  // columns (Order ID, Date, Property Address, Status, Supplier, Amount, Paid, Balance, Payment Method)
  const columnHelper = createColumnHelper<Row>();
  const columns = useMemo(() => [
    columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
    columnHelper.accessor("order_id", {
      header: () => <div className="th-text">Order ID</div>,
      cell: (info) => <div className="fw-medium text-sm">{info.getValue() ? `#${info.getValue()}` : "--"}</div>,
    }),
    columnHelper.accessor("date", {
      header: () => <div className="th-text">Date</div>,
      cell: (info) => {
        const d = info.getValue();
        if (!d) return <div className="text-sm">--</div>;
        try {
          const formatted = new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
          return <div className="text-sm">{formatted}</div>;
        } catch {
          return <div className="text-sm">--</div>;
        }
      },
    }),
    columnHelper.accessor("property_address", {
      header: () => <div className="th-text">Property Address</div>,
      cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
    }),
    columnHelper.accessor("status", {
      header: () => <div className="th-text">Status</div>,
      cell: (info) => <div className="text-sm">{STATUS_LABEL(info.getValue())}</div>,
    }),
    columnHelper.accessor("supplier", {
      header: () => <div className="th-text">Supplier</div>,
      cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
    }),
    columnHelper.accessor("amount", {
      header: () => <div className="th-text">Amount</div>,
      cell: (info) => <div className="fw-medium text-sm">{fmtMoney(Number(info.getValue() ?? 0))}</div>,
    }),
    columnHelper.accessor("paid", {
      header: () => <div className="th-text">Paid</div>,
      cell: (info) => <div className="text-sm" style={{ color: "#00a63e" }}>{fmtMoney(Number(info.getValue() ?? 0))}</div>,
    }),
    columnHelper.accessor("balance", {
      header: () => <div className="th-text">Balance</div>,
      cell: (info) => {
        const overPaid = info.row.original.over_paid;
        const balanceValue = Number(info.getValue() ?? 0);
        return (
          <div className="text-sm fw-medium" style={{ color: overPaid ? "#00a63e" : "#e7000b" }}>
            {overPaid ? `+${fmtMoney(balanceValue)}` : `-${fmtMoney(balanceValue)}`}
          </div>
        );
      },
    }),
    // columnHelper.accessor("payment_method", {
    //   header: () => <div className="th-text">Payment Method</div>,
    //   cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
    // }),
  // eslint-disable-next-line
  ], [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: data || [],
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: { pagination },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
  });

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pagination.pageIndex]);

  return (
    <div className="content" ref={topRef}>
      <div className="mb-4">
        <div>
          <h4 className="mb-1 po-title">Removal Orders Summary</h4>
          <p className="po-subtitle">Your removal orders billing & payments</p>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard label="Total Orders" value={cards?.total_orders ?? 0} money={false} accent="blue" icon="ri-settings-3-line" />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard label="Total Amount" value={cards?.total_revenue ?? 0} money accent="green" icon="bi-graph-up-arrow" />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard label="Paid" value={cards?.amount_paid ?? 0} money accent="green" icon="bi-currency-dollar" />
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <StatCard label="Balance" value={cards?.outstanding_balance ?? 0} money accent="red" icon="bi-exclamation-octagon" />
        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Order ID, Property Address..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="agent-removal-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        <div id="agent-removal-filters" className={`po-filters ${filtersOpen ? "show" : ""}`} style={{ overflow: "visible" }}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-lg-2">
                <label className="form-label mb-1 label-1">Date Range</label>
                <Select options={DATE_OPTIONS} value={datePreset} onChange={(v) => v && setDatePreset(v)} components={{ DropdownIndicator }} styles={customSelectStyles} classNamePrefix="rs" />
              </div>

              {datePreset.value === "custom" && (
                <>
                  <div className="col-lg-2">
                    <label className="form-label mb-1 label-1">From</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={customFrom} 
                      onChange={(e) => setCustomFrom(e.target.value)}
                      min={dateRangeLimits.minDate}
                      max={dateRangeLimits.maxDate}
                    />
                  </div>
                  <div className="col-lg-2">
                    <label className="form-label mb-1 label-1">To</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={customTo} 
                      onChange={(e) => setCustomTo(e.target.value)}
                      min={dateRangeLimits.minDate}
                      max={dateRangeLimits.maxDate}
                    />
                  </div>
                </>
              )}

              <div className="col-lg-auto">
                <label className="form-label mb-1 label-1">Job Status</label>
                <Select
                  options={STATUS_OPTIONS}
                  value={selectedStatuses}
                  onChange={(v) => setSelectedStatuses((v as StatusOption[]) || [])}
                  isMulti
                  closeMenuOnSelect={false}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                  placeholder="Select status"
                />
              </div>

              <div className="col-lg-1">
                <label className="form-label mb-1 label-1">Supplier</label>
                <Select
                  options={supplierOptions}
                  value={selectedSupplier}
                  onChange={(v) => setSelectedSupplier((v as SelectOption) || { label: "All", value: "" })}
                  isSearchable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                  placeholder="Select Supplier"
                />
              </div>

              <div className="col-lg-1">
                <label className="form-label mb-1 label-1">Payment Status</label>
                <Select options={PAYMENT_STATUS_OPTIONS} value={paymentStatus} onChange={(v) => v && setPaymentStatus(v)} components={{ DropdownIndicator }} styles={customSelectStyles} classNamePrefix="rs" />
              </div>

              {/* <div className="col-lg-1">
                <label className="form-label mb-1 label-1">Payment Method</label>
                <Select options={PAYMENT_METHOD_OPTIONS} value={paymentMethod} onChange={(v) => v && setPaymentMethod(v)} components={{ DropdownIndicator }} styles={customSelectStyles} classNamePrefix="rs" />
              </div> */}

              <div className="col-lg-auto d-flex align-items-end gap-2">
                <button className="btn btn-primary table-addbutton br-8" onClick={handleGenerate}>Apply Filter</button>
                <button className="btn back-btn surface-btn fw-bold fs-16" onClick={clearFilters}>Clear</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i className="ri-file-text-line me-2" aria-hidden="true" style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }} />
            <h5 className="mb-0">Removal Orders</h5>
          </div>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />

        {/* Desktop / Tablet */}
        <div className="rdt-desktop">
          <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* Mobile */}
        <div className="rdt-mobile">
          <MobileCardsOnly table={table} idColId="order_id" titleColId="supplier" actionColId="balance" />
        </div>
      </div>

      <div className="mt-4 mb-4">
        <TablePaginationControls table={table} />
      </div>
    </div>
  );
}